import { Alert, Box, Paper, Stack, Typography } from "@mui/material";
import { alpha, lighten } from "@mui/material/styles";
import { ChartData, Point } from "chart.js";
import "chart.js/auto";
import { ReactNode, useMemo } from "react";
import { Line } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import { useCurrency } from "../../context/CurrencyContext.tsx";
import { useTheme } from "../../context/ThemeContext";
import { OutcomesForParty } from "../../shared/Analytics";
import { ShownOutcome } from "../../shared/adjustedView";
import { Stage } from "../../shared/CaseConfiguration";
import { PartyDescriptors } from "../../shared/CaseModel";
import { IssueDescriptors } from "./AnalyticsProps";
import { whatHappened } from "./riskTooltip";
import { percentage } from "../../shared/percentage";
import { getTooltip } from "../../utils/chartjsTheming";
import { Metric } from "../general/dataAtoms/Metric";
import { NO_VALUE } from "../general/dataAtoms/shared";
import { SectionLabel } from "../general/SectionLabel";
import { getMaxAbsoluteValue } from "./ChartsUtility";
import {
  RISK_CHART_HEIGHT,
  RISK_LINE_POINTS,
  riskChartLegend,
  riskChartScales,
  settlementLegendEntry,
  settlementShade,
  SETTLEMENT_SHADING,
} from "./riskChart";
import { buildRiskSeries, RiskSeries } from "./riskSeries";
import { ChanceArrow, useVsLitigation, VsLitigationLine } from "./VsLitigation";

export const varianceFormatter = new Intl.NumberFormat("en-GB", {
  minimumFractionDigits: 1,
  maximumFractionDigits: 3,
});

/**
 * How far the series of a party's risk are lightened from the primary colour:
 * the enforcement result as an area with its edge, the net result as the
 * darker line over it.
 */
const ENFORCEMENT_AREA_LIGHTEN = 0.6;
const ENFORCEMENT_EDGE_LIGHTEN = 0.4;
const NET_RESULT_LIGHTEN = 0.2;

/** How much of its colour the area of the enforcement result takes. */
const ENFORCEMENT_AREA_ALPHA = 0.45;

/** The room the scale leaves over the highest and under the lowest amount. */
const SCALE_HEADROOM = 1.1;

export const OutcomeRiskAnalysis = ({
  outcome,
  parties = {},
  showAverageEV = false,
  litigated,
  other,
  issues = {},
  onOpen,
}: {
  outcome: OutcomesForParty<ShownOutcome>;
  parties?: PartyDescriptors;
  showAverageEV?: boolean;
  // in the settlement-adjusted view, the party's outcomes as litigated
  litigated?: OutcomesForParty<ShownOutcome>;
  // the outcomes of the other view, which the scale spans too, so that
  // switching between the views moves only the steps
  other?: OutcomesForParty<ShownOutcome>;
  // the claims, by which the tooltip says what an outcome decided
  issues?: IssueDescriptors;
  // opens the details of an outcome, as a step of the chart is clicked
  onOpen?: (outcome: ShownOutcome) => void;
}) => {
  const { theme } = useTheme();
  const { currency } = useCurrency();
  const { t } = useTranslation(undefined, { keyPrefix: "case.analytics.risk" });
  const party = parties[outcome.partyId];
  const { main: primary } = theme.palette.primary;

  const series = useMemo(
    () => buildRiskSeries(outcome, party?.assetLimit),
    [outcome, party?.assetLimit],
  );
  const {
    sortedJudgments,
    settlementBands,
    judgmentEvs,
    netResults,
    averageEnforcement,
    bestEnforcement,
    worstEnforcement,
    assetLimitReached,
    assetLimitReachedProbability,
  } = series;
  const litigatedSeries = useMemo(
    () => litigated && buildRiskSeries(litigated, party?.assetLimit),
    [litigated, party?.assetLimit],
  );
  const otherSeries = useMemo(
    () => other && buildRiskSeries(other, party?.assetLimit),
    [other, party?.assetLimit],
  );
  const vsLitigation = useVsLitigation();
  const shade = settlementShade(theme);

  if (sortedJudgments.length === 0)
    return (
      <Typography sx={{ color: "text.secondary" }}>
        {t("description", { count: 0 })}
      </Typography>
    );

  const chartData: ChartData<"line", (number | Point)[]> = {
    datasets: [
      {
        label: t("chart.enforcementResult"),
        data: judgmentEvs,
        stepped: true,
        fill: true,
        order: 2,
        backgroundColor: alpha(
          lighten(primary, ENFORCEMENT_AREA_LIGHTEN),
          ENFORCEMENT_AREA_ALPHA,
        ),
        borderColor: lighten(primary, ENFORCEMENT_EDGE_LIGHTEN),
        borderWidth: 2,
        ...RISK_LINE_POINTS,
      },
      {
        label: t("chart.netResult"),
        data: netResults,
        stepped: true,
        fill: false,
        order: 1,
        // a line has no area: its box in the legend stays empty
        backgroundColor: "transparent",
        borderColor: lighten(primary, NET_RESULT_LIGHTEN),
        borderWidth: 2.5,
        borderDash: [7, 5],
        ...RISK_LINE_POINTS,
      },
    ],
  };

  if (settlementBands.length > 0)
    chartData.datasets.push(
      settlementLegendEntry(t("chart.settlement"), shade),
    );

  if (showAverageEV)
    chartData.datasets.push({
      label: t("chart.caseValue"),
      data: [{ x: 0.5, y: averageEnforcement }],
      order: 1,
      pointStyle: "circle",
      pointRadius: 6,
      pointHoverRadius: 10,
      pointHitRadius: 20,
      backgroundColor: theme.palette.primary.light,
      borderColor: theme.palette.primary.dark,
    });

  // the scale spans the other view too
  let maxChartValue = Math.max(
    getMaxAbsoluteValue(chartData),
    ...(otherSeries
      ? [...otherSeries.judgmentEvs, ...otherSeries.netResults].map(({ y }) =>
          Math.abs(y),
        )
      : []),
  );
  // if there is an asset value and it is less than double the highest value, show it
  if (
    party?.assetLimit !== undefined &&
    party?.assetLimit !== "unlimited" &&
    party.assetLimit < maxChartValue * 2
  ) {
    chartData.datasets.push({
      label: t("chart.assetLimit"),
      data: [
        { x: 0, y: -party.assetLimit },
        { x: 1, y: -party.assetLimit },
      ],
      order: 0,
      backgroundColor: "transparent",
      borderColor: theme.palette.data.negative.main,
      borderWidth: 2,
      ...RISK_LINE_POINTS,
    });
    // make sure to scale in case asset value is higher
    maxChartValue = Math.max(maxChartValue, getMaxAbsoluteValue(chartData));
  }

  const name = party?.name ?? outcome.partyId;
  // the outcome of a point of the lines; the last point closes them at 100%
  const outcomeAt = (index: number) =>
    sortedJudgments[Math.min(index, sortedJudgments.length - 1)];
  // what happened: with whom the party settled, and what the judgment decided
  const titleOf = (outcomeOf: ShownOutcome | undefined) => {
    if (!outcomeOf) return [];
    const names = Object.fromEntries(
      Object.entries(parties).map(([id, { name: n }]) => [id, n]),
    );
    const { settledWith, won, lost } = whatHappened(
      outcomeOf,
      outcome.partyId,
      names,
      issues,
    );
    const stageName = (stage: Stage) =>
      t(Stage[stage], { keyPrefix: "case.stages" });
    if (outcomeOf.settledAt !== undefined)
      return [
        t("tooltip.settled", {
          opponents: settledWith.join(", "),
          stage: stageName(outcomeOf.settledAt),
        }),
      ];
    const decided = [
      ...(won.length > 0 ? [t("tooltip.won", { claims: won.join(", ") })] : []),
      ...(lost.length > 0
        ? [t("tooltip.lost", { claims: lost.join(", ") })]
        : []),
    ];
    return [
      ...(settledWith.length > 0
        ? [t("tooltip.settledWith", { opponents: settledWith.join(", ") })]
        : []),
      decided.length > 0
        ? t("tooltip.judgment", { decided: decided.join("; ") })
        : t("tooltip.judgmentOnly"),
    ];
  };
  // how likely the outcome is, how many are worse, and in litigation
  const standingOf = (index: number) => {
    const outcomeOf = outcomeAt(index);
    if (!outcomeOf) return [];
    const at = sortedJudgments.indexOf(outcomeOf);
    const worse = judgmentEvs[at]?.x ?? 0;
    const lines = [
      t("tooltip.likely", {
        probability: percentage.format(outcomeOf.probability),
        worse: percentage.format(worse),
      }),
    ];
    if (litigated && outcomeOf.settledAt === undefined) {
      const chance = litigated.outcomes
        .filter((o) => Math.abs(o.netResult - outcomeOf.netResult) < 0.005)
        .reduce((sum, { probability }) => sum + probability, 0);
      // one that takes a settlement with an opponent has no chance without
      lines.push(
        chance > 0
          ? vsLitigation.inLitigation(chance)
          : t("tooltip.onlySettling"),
      );
    }
    return lines;
  };

  // every figure has a line under it in both views, so switching moves nothing
  const compared = litigatedSeries && {
    positive: {
      chance: series.positiveOutcomesProbability,
      without: litigatedSeries.positiveOutcomesProbability,
    },
    worst: endAgainst(series, litigatedSeries, "worstEnforcement"),
    best: endAgainst(series, litigatedSeries, "bestEnforcement"),
  };
  const none = { text: "\u00a0" } as VsLitigationLine;
  const metrics: {
    label: string;
    value: string;
    line: VsLitigationLine;
    after?: ReactNode;
  }[] = [
    {
      label: t("summary.total"),
      value: String(sortedJudgments.length),
      line: none,
    },
    {
      label: t("summary.positive"),
      value: String(series.positiveOutcomes),
      line: none,
    },
    {
      label: t("summary.positiveProbability"),
      value: percentage.format(series.positiveOutcomesProbability),
      line: compared
        ? { text: vsLitigation.inLitigation(compared.positive.without) }
        : none,
      after: compared && (
        <ChanceArrow {...compared.positive} of={t("summary.positiveOf")} />
      ),
    },
    {
      label: t("summary.worst"),
      value:
        worstEnforcement === undefined
          ? NO_VALUE
          : currency.format(worstEnforcement),
      line: compared?.worst ? vsLitigation.end(compared.worst) : none,
    },
    {
      label: t("summary.best"),
      value:
        bestEnforcement === undefined
          ? NO_VALUE
          : currency.format(bestEnforcement),
      line: compared?.best ? vsLitigation.end(compared.best) : none,
    },
  ];

  return (
    <Stack spacing={2.25}>
      <Paper
        variant="outlined"
        sx={{ borderRadius: 3, px: 2.5, pt: 2, pb: 2.5 }}
      >
        {/* as the summary of the outcomes page: its title and what it is
            about, and at their end what to beware of */}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1.5,
          }}
        >
          <Box>
            <SectionLabel component="h2">{t("summary.title")}</SectionLabel>
            <Typography
              variant="body2"
              sx={{ mt: 0.5, color: "text.secondary" }}
            >
              {t("description", { count: sortedJudgments.length, name })}
            </Typography>
          </Box>
          {assetLimitReached > 0 && (
            <Alert severity="warning" sx={{ py: 0 }}>
              {t("warning.assetLimit", {
                name,
                count: assetLimitReached,
                probability: percentage.format(assetLimitReachedProbability),
              })}
            </Alert>
          )}
        </Box>
        <Box
          sx={{
            // as wide as each figure needs, wrapping where the row is full
            display: "flex",
            flexWrap: "wrap",
            columnGap: 3.5,
            rowGap: 2.25,
            mt: 2,
            "& > *": { flex: "1 1 170px", minWidth: "max-content" },
          }}
        >
          {metrics.map(({ label, value, line, after }) => (
            <Metric
              key={label}
              label={label}
              value={value}
              caption={line.text}
              captionTone={line.tone}
              after={after}
            />
          ))}
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ borderRadius: 3, px: 2.5, pt: 2, pb: 2 }}>
        <SectionLabel component="h2">{t("title")}</SectionLabel>
        <Box sx={{ height: RISK_CHART_HEIGHT }}>
          <Line
            data={chartData}
            plugins={[SETTLEMENT_SHADING]}
            options={{
              maintainAspectRatio: false,
              plugins: {
                legend: riskChartLegend(theme),
                settlementShading: { bands: settlementBands, color: shade },
                tooltip: {
                  ...getTooltip(theme),
                  // anywhere along a step, the outcome of the step
                  mode: "step",
                  intersect: false,
                  callbacks: {
                    title: ([item]) =>
                      item && titleOf(outcomeAt(item.dataIndex)),
                    beforeBody: ([item]) =>
                      item ? standingOf(item.dataIndex) : [],
                    label: (item) => {
                      const amount = currency.format(item.parsed.y ?? 0);
                      const settled =
                        outcomeAt(item.dataIndex)?.settledAt !== undefined;
                      const meaning =
                        item.dataset.label === t("chart.enforcementResult")
                          ? t(
                              settled
                                ? "tooltip.enforcementSettled"
                                : "tooltip.enforcement",
                            )
                          : item.dataset.label === t("chart.netResult")
                            ? t("tooltip.net", { name })
                            : undefined;
                      return `${item.dataset.label}: ${amount}${meaning ? ` — ${meaning}` : ""}`;
                    },
                    footer: () => (onOpen ? t("tooltip.details") : ""),
                  },
                },
              },
              interaction: { mode: "step", intersect: false },
              // a step opens the details of its outcome
              onClick: (_event, [element]) => {
                const outcomeOf = element && outcomeAt(element.index);
                if (onOpen && outcomeOf) onOpen(outcomeOf);
              },
              onHover: (event, elements) => {
                const target = event.native?.target as HTMLElement | undefined;
                if (target)
                  target.style.cursor =
                    onOpen && elements.length > 0 ? "pointer" : "default";
              },
              scales: riskChartScales({
                theme,
                currency,
                axisTitle: t("chart.axis"),
                // zero stays in the middle, so a loss reads as far down as a
                // gain of its size reads up
                y: {
                  min: -SCALE_HEADROOM * maxChartValue,
                  max: SCALE_HEADROOM * maxChartValue,
                },
              }),
            }}
          />
        </Box>
      </Paper>
    </Stack>
  );
};

/**
 * An end of the range of enforcement results against litigation: how far
 * settling moves it, and how likely it is with and without settlements.
 */
function endAgainst(
  shown: RiskSeries,
  litigated: RiskSeries,
  which: "worstEnforcement" | "bestEnforcement",
) {
  const end = shown[which];
  const litigatedEnd = litigated[which];
  if (end === undefined || litigatedEnd === undefined) return undefined;
  return {
    change: end - litigatedEnd,
    chance: chanceAt(shown, end),
    without: chanceAt(litigated, litigatedEnd),
  };
}

/** How likely an enforcement result is, to the cent. */
const chanceAt = (series: RiskSeries, balance: number) =>
  series.sortedJudgments
    .filter(
      (_, index) => Math.abs(series.judgmentEvs[index].y - balance) < 0.005,
    )
    .reduce((sum, { probability }) => sum + probability, 0);
